/**
 * In-memory cache for AI-generated narratives, keyed by a caller-chosen key + filter
 * state. Shared across FinTech Ops features (OE Report has its own copy scoped to that
 * feature; this one backs the reusable {@link AiNarrative} component).
 *
 * Narrative components unmount when their tab/section is hidden, so without this every
 * re-show would trigger a fresh Bedrock invocation. Scoped to the module (page lifetime)
 * on purpose: a full page reload should produce a fresh narrative from the latest data.
 */

const cache = new Map<string, string>();

/**
 * Cache key for a narrative under a given filter selection. `narrativeKey` identifies
 * the logical narrative (e.g. one per dashboard); `filters` is the JSON-encoded context
 * that, when changed, should invalidate the cached text.
 */
export const buildNarrativeCacheKey = (narrativeKey: string, filters: string): string => `${narrativeKey}|${filters}`;

export const getCachedNarrative = (key: string): string | undefined => cache.get(key);

export const setCachedNarrative = (key: string, value: string): void => {
  cache.set(key, value);
};

/** Drop a cached narrative so the next render regenerates it. */
export const clearCachedNarrative = (key: string): void => {
  cache.delete(key);
};
